import { memo, useCallback, useMemo } from 'react';

import { ShortUrlObjectType } from '@air/api/types';
import { Skeleton } from '@air/component-skeleton';
import { useAirModal } from '@air/provider-modal';
import { formatFullName } from '@air/utilities';

import { SharePrivateCollectionRegenerate } from '~/components/SharePrivateCollectionModal/components/SharePrivateCollectionRegenerate';
import { EditSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/EditSharePrivateCollectionModal';
import { useGetShortUrlForCollection } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetShortUrlForCollection';
import { EditableSharePrivateLink } from '~/components/SharePrivateContentModal/EditableSharePrivateLink';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';

const TRACK_LOCATION = 'share-collection-edit-modal';

export type EditableCollectionSharePrivateLinkProps = {
  collectionId: string;
};

export const EditableCollectionSharePrivateLink = memo(({ collectionId }: EditableCollectionSharePrivateLinkProps) => {
  const { data: shortIdInfo, isPending } = useGetShortUrlForCollection({ collectionId });
  const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();
  const [showRegenerateModal, hideRegenerateModal] = useAirModal(SharePrivateCollectionRegenerate);
  const [showEditPrivateCollectionModal, hideEditPrivateCollectionModal] = useAirModal(EditSharePrivateCollectionModal);
  const { members } = useWorkspaceMembers();

  const createdBy = useMemo(
    () => members?.allMembers.find((member) => member.accountId === shortIdInfo?.createdBy),
    [members, shortIdInfo],
  );

  const onRegenerateClick = useCallback(() => {
    if (!shortIdInfo) {
      return;
    }

    hideEditPrivateCollectionModal();

    const showSharePrivateCollectionModal = () => {
      showEditPrivateCollectionModal({
        collectionId,
      });
    };

    showRegenerateModal({
      collectionId,
      shortIdInfo,
      trackLocation: TRACK_LOCATION,
      onRegenerated: () => {
        showSharePrivateCollectionModal();
        showTurnOnShareLinkNotifications({
          onManageClick: hideEditPrivateCollectionModal,
        });
        hideRegenerateModal();
      },
      onCancel: showSharePrivateCollectionModal,
    });
  }, [
    shortIdInfo,
    collectionId,
    hideEditPrivateCollectionModal,
    showRegenerateModal,
    showEditPrivateCollectionModal,
    showTurnOnShareLinkNotifications,
    hideRegenerateModal,
  ]);

  if (!shortIdInfo || isPending) {
    return <Skeleton className="h-12 w-full" />;
  }

  return (
    <div className="flex border-grey-4 border-t px-8 pt-6">
      <EditableSharePrivateLink
        createdAt={shortIdInfo.createdAt}
        createdBy={createdBy && formatFullName(createdBy.firstName, createdBy.lastName)}
        objectId={collectionId}
        objectType={ShortUrlObjectType.collection}
        onRegenerate={onRegenerateClick}
        shareId={shortIdInfo.id}
        trackLocation={TRACK_LOCATION}
      />
    </div>
  );
});

EditableCollectionSharePrivateLink.displayName = 'EditableCollectionSharePrivateLink';
